import { Group, Text } from '@mantine/core';
import type { User } from 'src/admin/users/types';
import { NO_ADVISOR_FILTER_VALUE } from 'src/clients/components/AdvisorFilterSelect';
import { DropdownButton, type DropdownButtonItem } from 'src/design-system/DropdownButton';
import type { Notification } from 'src/notifications/types/Notification';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatName, NameFormat } from 'src/utils/nameFormatting';

export default function AdvisorScopeDropdown({
  notifications,
  advisors,
  advisorOf,
  value,
  onChange,
}: {
  notifications: Notification[];
  advisors: User[];
  advisorOf: (notification: Notification) => string | null;
  value: string | null;
  onChange: (advisor: string | null) => void;
}) {
  const advisorBuckets = new Set(
    notifications.map(notification => advisorOf(notification) ?? NO_ADVISOR_FILTER_VALUE),
  );
  const relevantAdvisors = advisors.filter(advisor => advisorBuckets.has(advisor.id));
  const clientsLabel = (advisor: User): string =>
    `${formatName(advisor.firstName, advisor.lastName, NameFormat.NATURAL)}'s clients`;

  const selectedAdvisor = advisors.find(advisor => advisor.id === value);
  let advisorLabel = 'All clients';
  if (value === NO_ADVISOR_FILTER_VALUE) {
    advisorLabel = 'Unassigned clients';
  } else if (selectedAdvisor) {
    advisorLabel = clientsLabel(selectedAdvisor);
  }

  const advisorItems: DropdownButtonItem[] = [
    { label: 'All clients', onClick: () => onChange(null) },
    ...relevantAdvisors.map(advisor => ({
      label: clientsLabel(advisor),
      onClick: () => onChange(advisor.id),
    })),
  ];
  if (advisorBuckets.has(NO_ADVISOR_FILTER_VALUE)) {
    advisorItems.push({
      label: 'Unassigned clients',
      onClick: () => onChange(NO_ADVISOR_FILTER_VALUE),
    });
  }

  return (
    <Group gap={4} align="center">
      <Text c="gray.9" size="xs" fw={FONT_WEIGHTS.semibold}>
        Showing:
      </Text>
      <DropdownButton label={advisorLabel} items={advisorItems} ariaLabel="Filter by adviser" />
    </Group>
  );
}
